import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { Logo } from '../components/Logo';
import { LangSwitcher } from '../components/LangSwitcher';
import { AdSlot } from '../components/AdSlot';
import { PublicFooter } from '../components/PublicFooter';
import { Trophy, ChevronDown, ChevronUp, Zap, Target, Globe, BookOpen } from 'lucide-react';
import { useState, useEffect } from 'react';
import { CupSimulator } from '../components/simulator/CupSimulator';
import { EmbedSnippetSection } from '../components/embed/EmbedSnippetSection';
import { usePublicFreeLimits } from '../hooks/usePublicFreeLimits';
import { useLocalizedSeo } from '../hooks/useLocalizedSeo';
import { ogImageUrl } from '../config/og-cards';
import { localizedPath } from '../i18n/slugMap';
import type { AppLocale } from '../i18n/locales';
import type { FreeLimits } from '../types';

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface CompetitionRule {
  title: string;
  items: string[];
}

export interface SimuladorLandingConfig {
  slug: string;
  /** Release C — id da rota em ROUTE_SLUGS: deriva canonical/hreflang por idioma */
  routeId: string;
  competitionExternalId: number;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  /** URL canônica (fallback estático PT-BR) */
  canonical: string;
  heroTitle: string;
  heroSubtitle: string;
  /** Cor de destaque (hex) para gradient do hero */
  accentFrom: string;
  accentTo: string;
  /** Formato da competição (ex: "48 seleções em 12 grupos de 4") */
  formatDescription: string;
  /** Regras/regulamento da competição */
  rules: CompetitionRule[];
  /** Sobre a competição — conteúdo SEO rico */
  about: string[];
  /** Fatos/curiosidades */
  facts?: string[];
  /** FAQ */
  faqs: { question: string; answer: string | ((limits: FreeLimits) => string) }[];
  /** JSON-LD adicional (SportsEvent, etc.) */
  jsonLd?: object;
  /** routeId da landing de bolão relacionada — resolvido pra URL no idioma
   *  corrente via localizedPath (ex: 'pool-copa-do-mundo'). */
  bolaoRouteId?: string;
  /** Label da landing de bolão relacionada */
  bolaoLabel?: string;
}

// ─── Cross-links entre simuladores ───────────────────────────────────────────
//
// `routeId` referencia ROUTE_SLUGS (apps/web/src/i18n/slugMap.ts) — o href
// final é gerado em runtime via localizedPath(routeId, locale) pra abrir no
// idioma corrente. Slug PT mantido em `slug` apenas pra filtrar a página
// atual da lista.

const OTHER_SIMULATORS = [
  { labelKey: 'interlink.sim.copa-do-mundo', label: 'Simulador Copa do Mundo 2026', routeId: 'sim-copa-do-mundo', slug: 'simulador-copa-do-mundo-2026' },
  { labelKey: 'interlink.sim.libertadores', label: 'Simulador Libertadores 2026', routeId: 'sim-libertadores', slug: 'simulador-libertadores-2026' },
  { labelKey: 'interlink.sim.sudamericana', label: 'Simulador Sudamericana 2026', routeId: 'sim-sudamericana', slug: 'simulador-copa-sudamericana-2026' },
];

const OTHER_BOLOES = [
  { labelKey: 'interlink.bolao.copa-do-mundo', label: 'Bolão Copa do Mundo 2026', routeId: 'pool-copa-do-mundo' },
  { labelKey: 'interlink.bolao.brasileirao', label: 'Bolão Brasileirão 2026', routeId: 'pool-brasileirao' },
  { labelKey: 'interlink.bolao.libertadores', label: 'Bolão Libertadores 2026', routeId: 'pool-libertadores' },
  { labelKey: 'interlink.bolao.champions-league', label: 'Bolão Champions League 2026', routeId: 'pool-champions-league' },
];

// ─── Componentes auxiliares ──────────────────────────────────────────────────

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-gray-900">{question}</span>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-sm text-gray-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

function RuleSection({ rule }: { rule: CompetitionRule }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-primary-600" />
          {rule.title}
        </span>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4">
          <ul className="space-y-2">
            {rule.items.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed">
                <span className="w-5 h-5 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export function SimuladorLanding({ config }: { config: SimuladorLandingConfig }) {
  const { t, i18n } = useTranslation('landing');
  // Locale corrente normalizado pra resolver hrefs via localizedPath (cross-
  // links no idioma da página atual em vez do default PT).
  const locale: AppLocale =
    i18n.language === 'en' ? 'en'
      : i18n.language === 'es' ? 'es'
      : 'pt-BR';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { limits } = usePublicFreeLimits();
  const seo = useLocalizedSeo(config.routeId);

  // Prefixo i18n por landing — namespace 'landing', chaves sim.<slug>.*
  const ck = (suffix: string, def: string) => t(`sim.${config.slug}.${suffix}`, def);

  const resolvedFaqs = config.faqs.map((faq, i) => {
    const rawAnswer =
      typeof faq.answer === 'function' ? faq.answer(limits) : faq.answer;
    return {
      question: ck(`faq_${i}_q`, faq.question),
      answer: t(`sim.${config.slug}.faq_${i}_a`, {
        defaultValue: rawAnswer,
        ...limits,
      }),
    };
  });

  // Regras: título e itens de cada seção resolvidos via t().
  const resolvedRules = config.rules.map((rule, r) => ({
    title: ck(`rule_${r}_title`, rule.title),
    items: rule.items.map((item, i) => ck(`rule_${r}_item_${i}`, item)),
  }));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const navLinks = [
    { label: t('sim_chrome.nav_simulator', 'Simulador'), href: '#simulador' },
    { label: t('sim_chrome.nav_rules', 'Regulamento'), href: '#regulamento' },
    { label: t('sim_chrome.nav_embed', 'Incorporar'), href: '#incorporar', highlight: true },
    { label: t('chrome.nav_faq', 'FAQ'), href: '#faq' },
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: config.seoTitle,
      description: config.seoDescription,
      url: seo.canonical,
      datePublished: '2026-03-24',
      dateModified: '2026-04-27',
      inLanguage: seo.locale,
      author: { '@id': 'https://bolaoelite.com.br/#organization' },
      publisher: { '@id': 'https://bolaoelite.com.br/#organization' },
      isPartOf: { '@id': 'https://bolaoelite.com.br/#website' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: config.heroTitle,
      applicationCategory: 'SportsApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'BRL' },
      description: config.seoDescription,
      datePublished: '2026-03-24',
      dateModified: '2026-04-27',
      inLanguage: seo.locale,
      author: { '@id': 'https://bolaoelite.com.br/#organization' },
      publisher: { '@id': 'https://bolaoelite.com.br/#organization' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: seo.locale,
      datePublished: '2026-03-24',
      dateModified: '2026-04-27',
      mainEntity: resolvedFaqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer },
      })),
    },
    ...(config.jsonLd ? [config.jsonLd] : []),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={ck('seo_title', config.seoTitle)}
        description={ck('seo_desc', config.seoDescription)}
        keywords={ck('seo_keywords', config.seoKeywords)}
        canonical={seo.canonical}
        ogUrl={seo.ogUrl}
        ogImage={ogImageUrl(config.routeId, seo.locale)}
        locale={seo.locale}
        alternates={seo.alternates}
        jsonLd={jsonLd}
      />

      {/* ─── HEADER ─── */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <Logo showText={true} imgClassName="h-12 md:h-20 w-auto object-contain" />
            </Link>

            <nav className="hidden md:flex items-center gap-8 mx-auto">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className={
                    link.highlight
                      ? 'text-sm font-bold text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-1'
                      : 'text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors'
                  }
                >
                  {link.highlight && <span aria-hidden="true">✨</span>}
                  {link.label}
                </a>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-4 shrink-0">
              <LangSwitcher />
              <Link
                to="/auth"
                className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-sm"
              >
                {t('chrome.cta_register', 'Cadastrar Grátis')}
              </Link>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden ml-auto p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label={t('chrome.menu_aria', 'Menu')}
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-100 py-4 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className={
                    link.highlight
                      ? 'block px-2 py-2.5 text-sm font-bold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors'
                      : 'block px-2 py-2.5 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors'
                  }
                >
                  {link.highlight && <span aria-hidden="true">✨ </span>}
                  {link.label}
                </a>
              ))}
              <div className="pt-3 space-y-3">
                <div className="flex justify-center">
                  <LangSwitcher />
                </div>
                <Link
                  to="/auth"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full block text-center py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all"
                >
                  {t('chrome.cta_register', 'Cadastrar Grátis')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* ─── HERO ─── */}
      <section
        className="text-white py-20 px-4"
        style={{ background: `linear-gradient(135deg, ${config.accentFrom}, ${config.accentTo})` }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <Target className="w-4 h-4 text-yellow-300" />
            <span className="text-sm font-medium">{t('sim_chrome.badge', 'Simulador Interativo')}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4">
            {ck('hero_title', config.heroTitle)}
          </h1>
          <p className="text-lg md:text-xl text-white/80 mb-4 leading-relaxed">
            {ck('hero_subtitle', config.heroSubtitle)}
          </p>
          <p className="text-sm text-white/60 mb-8">{ck('format_desc', config.formatDescription)}</p>
          <a
            href="#simulador"
            onClick={(e) => handleAnchorClick(e, '#simulador')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-bold text-lg rounded-2xl shadow-2xl hover:shadow-yellow-400/30 transition-all"
          >
            <Zap className="w-5 h-5" />
            {t('sim_chrome.hero_cta', 'Simular Agora')}
          </a>
        </div>
      </section>

      <AdSlot slotName="competition_landing_top" format="horizontal" area="public" className="max-w-5xl mx-auto px-4 py-6" />

      {/* ─── SIMULADOR INTERATIVO ─── */}
      <section id="simulador" className="py-16 px-4 bg-slate-900 scroll-mt-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {t('sim_chrome.section_title', 'Simulador Interativo')}
            </h2>
            <p className="text-slate-400 text-sm">
              {t('sim_chrome.section_desc', 'Monte seu bracket, escolha os classificados de cada grupo e simule o caminho até a grande final.')}
            </p>
          </div>
          <CupSimulator competitionExternalId={config.competitionExternalId} />
        </div>
      </section>

      <AdSlot slotName="simulator_landing_inline" format="rectangle" area="public" className="max-w-5xl mx-auto px-4 py-6" />

      {/* ─── SOBRE A COMPETIÇÃO ─── */}
      {config.about.length > 0 && (
        <section className="py-14 px-4 bg-gray-50 border-y border-gray-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center flex items-center justify-center gap-2">
              <Globe className="w-6 h-6 text-primary-600" />
              {t('sim_chrome.about_title', 'Sobre a Competição')}
            </h2>
            <div className="space-y-4">
              {config.about.map((paragraph, i) => (
                <p key={i} className="text-sm text-gray-700 leading-relaxed">{ck(`about_${i}`, paragraph)}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── FATOS / CURIOSIDADES ─── */}
      {config.facts && config.facts.length > 0 && (
        <section className="py-14 px-4 bg-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
              {t('sim_chrome.facts_title', 'Curiosidades e Destaques')}
            </h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {config.facts.map((fact, i) => (
                <li key={i} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <Zap className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700 leading-relaxed">{ck(`fact_${i}`, fact)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ─── REGULAMENTO / REGRAS ─── */}
      <section id="regulamento" className="py-16 px-4 bg-gray-50 scroll-mt-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 text-center">
            {t('sim_chrome.rules_title', 'Regulamento e Regras')}
          </h2>
          <p className="text-sm text-gray-500 text-center mb-8">
            {t('sim_chrome.rules_desc', 'Entenda o formato da competição, critérios de classificação e regras do mata-mata.')}
          </p>
          <div className="space-y-3">
            {resolvedRules.map((rule, r) => (
              <RuleSection key={r} rule={rule} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── INCORPORE EM SEU SITE ─── */}
      <EmbedSnippetSection
        embedSlug={config.slug}
        widgetLabel={config.heroTitle}
      />

      {/* ─── FAQ ─── */}
      <section id="faq" className="py-16 px-4 bg-white scroll-mt-16">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">
            {t('chrome.faq_title', 'Perguntas frequentes')}
          </h2>
          <div className="space-y-3">
            {resolvedFaqs.map(faq => (
              <FaqItem key={faq.question} {...faq} />
            ))}
          </div>
        </div>
      </section>

      <AdSlot slotName="competition_landing_bottom" format="horizontal" area="public" className="max-w-5xl mx-auto px-4 py-6" />

      {/* ─── CTA: CRIAR BOLÃO ─── */}
      <section
        className="py-20 px-4 text-white text-center"
        style={{ background: `linear-gradient(135deg, ${config.accentFrom}, ${config.accentTo})` }}
      >
        <div className="max-w-xl mx-auto">
          <h2 className="text-3xl font-extrabold mb-4">{t('sim_chrome.final_cta_title', 'Quer disputar com amigos?')}</h2>
          <p className="text-white/80 mb-8">
            {t('sim_chrome.final_cta_text', 'Crie seu bolão grátis no Bolão Elite e dispute palpites em tempo real.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/auth"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-bold rounded-2xl shadow-xl transition-all"
            >
              <Trophy className="w-5 h-5" />
              {t('sim_chrome.final_cta_button', 'Criar Bolão Grátis')}
            </Link>
            {config.bolaoRouteId && (
              <Link
                to={localizedPath(config.bolaoRouteId, locale)}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-2xl border border-white/30 transition-all"
              >
                {config.bolaoLabel
                  ? ck('bolao_label', config.bolaoLabel)
                  : t('sim_chrome.bolao_default_label', 'Ver Bolão')}
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* ─── OUTROS SIMULADORES — interlinks SEO ─── */}
      <section className="py-14 px-4 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 text-center">
            {t('sim_chrome.others_title', 'Outros Simuladores')}
          </h2>
          <p className="text-sm text-gray-500 text-center mb-6">
            {t('sim_chrome.others_desc', 'Simule o bracket de outras competições de copa.')}
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
            {OTHER_SIMULATORS.filter(s => s.slug !== config.slug).map((sim) => (
              <li key={sim.slug}>
                <Link
                  to={localizedPath(sim.routeId, locale)}
                  className="flex items-center justify-center text-center px-3 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:border-primary-400 hover:text-primary-700 hover:bg-primary-50 transition-all"
                >
                  {t(sim.labelKey, sim.label)}
                </Link>
              </li>
            ))}
          </ul>

          <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">
            {t('sim_chrome.boloes_title', 'Bolões Disponíveis')}
          </h3>
          <p className="text-sm text-gray-500 text-center mb-6">
            {t('sim_chrome.boloes_desc', 'Crie bolões para os principais campeonatos do Brasil e do mundo.')}
          </p>
          <ul className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {OTHER_BOLOES.map((comp) => (
              <li key={comp.routeId}>
                <Link
                  to={localizedPath(comp.routeId, locale)}
                  className="flex items-center justify-center text-center px-3 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:border-primary-400 hover:text-primary-700 hover:bg-primary-50 transition-all"
                >
                  {t(comp.labelKey, comp.label)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

export default SimuladorLanding;
